/* eslint-disable jira/filename-case */
import isUndefined from 'lodash/isUndefined';
import merge from 'lodash/merge';
import omitBy from 'lodash/omitBy';
import uniq from 'lodash/uniq';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';

const extractFromEventContext = (propertyNames: string[], event: UIAnalyticsEvent) =>
	event.context // oxlint-disable-next-line @typescript-eslint/no-explicit-any
		.reduce<Array<any>>((acc: any[], contextItem: any) => {
			propertyNames.forEach((propertyName) => {
				acc.push(contextItem[propertyName]);
			});
			return acc;
		}, [])
		.filter(Boolean);

const extractFromEventPayload = (propertyName: string, event: UIAnalyticsEvent) =>
	event.payload[propertyName];

const extractFromPayloadOrContext = (propertyName: string, event: UIAnalyticsEvent): unknown[] => {
	const payloadProp = extractFromEventPayload(propertyName, event);
	const contextProps = extractFromEventContext([propertyName], event);
	return payloadProp ? contextProps.concat(payloadProp) : contextProps;
};

const extractPayloadAttributes = (event: UIAnalyticsEvent) => {
	const {
		tags,
		actionSubjectId,
		containerType,
		containerId,
		objectType,
		objectId,
		analyticsType,
		name,
		action,
		...attributes
	} = event.payload;

	return attributes;
};

/**
 * Temporary using 'component' and 'componentName' fields.
 * This should be replaced with 'componentName' only once we bump all the ak components.
 */
export const getComponents = (event: UIAnalyticsEvent): string[] =>
	extractFromEventContext(['component', 'componentName'], event);

export const getActionSubjects = (event: UIAnalyticsEvent): string[] =>
	extractFromEventContext(['actionSubject'], event);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getSources = (event: UIAnalyticsEvent): any[] =>
	extractFromEventContext(['source'], event);

export const getAttributes = (
	event: UIAnalyticsEvent,
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> & {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	[x: string]: any;
} =>
	merge(
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		extractFromEventContext(['attributes'], event).reduce<Record<string, any>>(
			(result, extraAttributes) => merge(result, extraAttributes),
			{},
		),
		extractPayloadAttributes(event),
	);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getTags = (event: UIAnalyticsEvent): Record<any, any>[] | undefined => {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const tags = uniq<Record<any, any>>(
		extractFromEventContext(['tags'], event) // oxlint-disable-next-line @typescript-eslint/no-explicit-any
			.reduce<Array<any>>((result, extraTags) => result.concat(extraTags), [])
			.concat(extractFromEventPayload('tags', event))
			.filter(Boolean),
	);
	return tags.length > 0 ? tags : undefined;
};

export const getContainerTypes = (event: UIAnalyticsEvent): unknown[] =>
	extractFromPayloadOrContext('containerType', event);

export const getContainerIds = (event: UIAnalyticsEvent): unknown[] =>
	extractFromPayloadOrContext('containerId', event);

export const getObjectTypes = (event: UIAnalyticsEvent): unknown[] =>
	extractFromPayloadOrContext('objectType', event);

export const getObjectIds = (event: UIAnalyticsEvent): unknown[] =>
	extractFromPayloadOrContext('objectId', event);

// singular extractors

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getActionSubjectId = (event: UIAnalyticsEvent): any =>
	extractFromEventPayload('actionSubjectId', event);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getAnalyticsType = (event: UIAnalyticsEvent): any =>
	extractFromEventPayload('analyticsType', event);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getName = (event: UIAnalyticsEvent): any => extractFromEventPayload('name', event);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getAction = (event: UIAnalyticsEvent): any => extractFromEventPayload('action', event);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getContainers = (event: UIAnalyticsEvent): Partial<any> | undefined => {
	const contextContainers = extractFromEventContext(['containers'], event);
	const payloadContainers = event.payload.containers;
	const allContainers = merge(
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		contextContainers.reduce<Record<string, any>>(
			(result, extraContainers) => merge(result, extraContainers),
			{},
		),
		payloadContainers,
	);

	const finalContainers = omitBy(allContainers, isUndefined);
	return Object.keys(finalContainers).length > 0 ? finalContainers : undefined;
};
